import { useEffect, useState, type JSX } from 'react';
import { PlusIcon, Trash2Icon, XIcon } from './icons.js';
import { apiGet, apiDelete, ApiError } from '../api/client.js';

/**
 * Slice 3b chat-history wedge — left-rail list component.
 *
 * Renders chats grouped by `Today / Yesterday / This week / Earlier`.
 * Title hierarchy per `/plan-design-review` D1A: title primary, question
 * preview secondary, timestamp + message-count metadata on right.
 *
 * Auto-title transition (D2A): pending titles render italic-gray; once the
 * server flips to `generated` the row cross-fades on next list refresh.
 *
 * Trash visibility (D7A): hover-reveal on desktop (md:opacity-0 →
 * group-hover:opacity-100); always-visible on mobile.
 *
 * Inline two-click delete (D2B): clicking trash swaps the row content to
 * `Are you sure? [Delete] [Cancel]`. No modal.
 */

interface ChatSummaryWire {
  chatId: string;
  title: string;
  titleStatus: 'pending' | 'generating' | 'generated' | 'failed';
  messageCount: number;
  sourceCount: number;
  lastQuestion: string;
  lastAnswerPreview: string;
  createdAt: string;
  updatedAt: string;
}

const TITLE_FALLBACK_AFTER_MS = 5 * 60 * 1000;
const TITLE_FALLBACK_MAX_CHARS = 50;

interface ListResponse {
  chats: ChatSummaryWire[];
  nextCursor: string | null;
}

interface ChatHistoryRailProps {
  currentChatId: string | null;
  onPickChat: (chatId: string) => void;
  onNewChat: () => void;
  /**
   * Bumped by the parent every time a chat is created or a turn finishes,
   * so the rail refetches and the new chat appears at the top of `Vandaag`.
   */
  refreshKey: number;
  /**
   * Mobile drawer state. The rail is always visible on md+ regardless;
   * `open` controls only the off-canvas behaviour at < md.
   */
  open?: boolean;
  onClose?: () => void;
  /**
   * Desktop collapse — when true, the rail hides on md+ (mobile drawer
   * behaviour is unchanged). The parent owns the toggle button so it can
   * be co-located with the chat content header. Issue #3 UX pass.
   */
  desktopCollapsed?: boolean;
}

const PAGE_SIZE = 50;

export function ChatHistoryRail({
  currentChatId,
  onPickChat,
  onNewChat,
  refreshKey,
  open = false,
  onClose,
  desktopCollapsed = false,
}: ChatHistoryRailProps): JSX.Element {
  const [chats, setChats] = useState<ChatSummaryWire[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Initial load + every refreshKey bump.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet<ListResponse>('/api/v1/chats', { limit: PAGE_SIZE });
        if (cancelled) return;
        setChats(res.chats);
        setNextCursor(res.nextCursor);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? 'Could not load history.' : 'Could not load history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await apiGet<ListResponse>('/api/v1/chats', {
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setChats((prev) => [...prev, ...res.chats]);
      setNextCursor(res.nextCursor);
    } catch {
      setError('Failed to load more history.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function confirmDelete(chatId: string): Promise<void> {
    try {
      await apiDelete(`/api/v1/chats/${chatId}`);
      setChats((prev) => prev.filter((c) => c.chatId !== chatId));
      setConfirmDeleteId(null);
      if (currentChatId === chatId) onNewChat();
    } catch {
      setError('Delete failed. Please try again.');
      setConfirmDeleteId(null);
    }
  }

  // Hide empty draft rows ("New conversation… 0 msg") that aren't the
  // active chat — they clutter the rail and confuse users.
  const visibleChats = chats.filter((c) => c.messageCount > 0 || c.chatId === currentChatId);
  const groups = groupByBucket(visibleChats);

  return (
    <>
      {/* Mobile scrim — only when drawer is open at < md. */}
      {open && (
        <button
          type="button"
          aria-label="Close history"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-nebula-fg-1/40 md:hidden"
        />
      )}
      <aside
        aria-label="Recent conversations"
        className={`fixed inset-y-0 left-0 z-40 flex w-[280px] shrink-0 flex-col border-r border-nebula-border-soft bg-nebula-surface transition-transform duration-200 md:static md:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${desktopCollapsed ? 'md:hidden' : ''}`}
      >
        {/* Mobile-only header strip with the close button — sits ABOVE the
            "New conversation" button so the two don't overlap. Hidden on md+. */}
        <div className="flex items-center justify-between border-b border-nebula-border-soft px-3 py-2 md:hidden">
          <span className="ov-meta-upper">History</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close history"
            className="rounded-md p-1.5 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
          >
            <XIcon size={18} />
          </button>
        </div>
        <div className="border-b border-nebula-border-soft px-4 py-3">
          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-nebula-primary px-3 py-2 text-sm font-medium font-ui text-white transition hover:bg-nebula-secondary"
          >
            <PlusIcon className="h-4 w-4" /> New conversation
          </button>
          {/* "Recent" heading is hidden on mobile (already shown in the top
              strip) but stays visible on desktop where the rail has no top
              strip. */}
          <h2 className="ov-meta-upper mt-4 hidden md:block">Recent</h2>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll">
          {loading && chats.length === 0 && <SkeletonRows />}

          {!loading && chats.length === 0 && error === null && (
            <div className="px-4 py-12 text-center">
              <p className="text-sm font-medium font-ui text-nebula-fg-1">No conversations yet.</p>
              <p className="mt-1 text-xs text-nebula-fg-3">
                Ask your first question to get started.
              </p>
            </div>
          )}

          {error !== null && (
            <div className="px-4 py-6">
              <p className="text-sm text-nebula-error">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="mt-2 text-xs text-nebula-secondary hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {groups.map((group, gi) => (
            <div
              key={group.label}
              className={gi > 0 ? 'border-t border-nebula-border-soft py-2' : 'py-2'}
            >
              <div className="ov-meta-upper sticky top-0 z-10 bg-nebula-surface px-4 py-1.5">
                {group.label}
              </div>
              <ul className="px-1.5">
                {group.chats.map((chat) => (
                  <li key={chat.chatId} className="group">
                    {confirmDeleteId === chat.chatId ? (
                      <ConfirmDeleteRow
                        onConfirm={() => void confirmDelete(chat.chatId)}
                        onCancel={() => setConfirmDeleteId(null)}
                      />
                    ) : (
                      <ChatRow
                        chat={chat}
                        isActive={currentChatId === chat.chatId}
                        onPick={() => onPickChat(chat.chatId)}
                        onAskDelete={() => setConfirmDeleteId(chat.chatId)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {nextCursor !== null && (
            <div className="px-3 py-3">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="w-full rounded-md py-2 text-xs font-medium text-nebula-fg-3 transition hover:bg-nebula-tint hover:text-nebula-fg-1 disabled:opacity-60"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

interface ChatRowProps {
  chat: ChatSummaryWire;
  isActive: boolean;
  onPick: () => void;
  onAskDelete: () => void;
}

function ChatRow({ chat, isActive, onPick, onAskDelete }: ChatRowProps): JSX.Element {
  const titleIsPending = chat.titleStatus !== 'generated';
  // Fallback to the first user message when title gen failed terminally,
  // OR the chat is older than 5 minutes and still hasn't titled. Stops
  // chats from being labelled "New conversation" forever when Gemini Flash
  // refused / errored / never landed.
  const ageMs = Date.now() - new Date(chat.createdAt).getTime();
  const useFallbackPreview =
    titleIsPending &&
    chat.lastQuestion.length > 0 &&
    (chat.titleStatus === 'failed' || ageMs > TITLE_FALLBACK_AFTER_MS);
  const fallbackLabel = useFallbackPreview
    ? chat.lastQuestion.length > TITLE_FALLBACK_MAX_CHARS
      ? `${chat.lastQuestion.slice(0, TITLE_FALLBACK_MAX_CHARS).trimEnd()}…`
      : chat.lastQuestion
    : null;
  return (
    <div
      className={`row-hover relative cursor-pointer rounded-md px-3 py-2 ${
        isActive ? 'bg-nebula-tint' : 'hover:bg-nebula-tint'
      }`}
    >
      <button
        type="button"
        onClick={onPick}
        className="block w-full text-left"
        aria-label={`Open conversation: ${fallbackLabel ?? chat.title}`}
      >
        <p
          className={`line-clamp-2 text-sm font-ui transition-opacity ${
            titleIsPending
              ? 'italic text-nebula-fg-3'
              : isActive
                ? 'font-medium text-nebula-primary'
                : 'font-medium text-nebula-fg-1'
          }`}
        >
          {fallbackLabel
            ? fallbackLabel
            : titleIsPending && chat.titleStatus === 'pending' && chat.messageCount === 0
              ? 'New conversation…'
              : chat.title}
        </p>
        {chat.lastQuestion && (
          <p className="mt-0.5 truncate text-xs text-nebula-fg-3">{chat.lastQuestion}</p>
        )}
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-nebula-fg-3">
          <span className="font-mono">{formatTime(chat.updatedAt)}</span>
          <span className="ov-numeric rounded bg-nebula-gray-5 px-1.5 py-0.5 text-nebula-fg-2">
            {chat.messageCount} {chat.messageCount === 1 ? 'message' : 'messages'}
          </span>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAskDelete();
        }}
        aria-label="Delete conversation"
        className="absolute right-2 top-2 rounded p-1 text-nebula-fg-3 opacity-100 transition hover:bg-nebula-error-bg hover:text-nebula-error md:opacity-0 md:group-hover:opacity-100"
      >
        <Trash2Icon className="h-4 w-4" />
      </button>
    </div>
  );
}

function ConfirmDeleteRow({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="rounded-md bg-nebula-error-bg px-3 py-2.5">
      <p className="text-sm font-medium font-ui text-nebula-fg-1">Are you sure?</p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-nebula-error px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
        >
          Delete
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-nebula-border-soft bg-nebula-surface px-3 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SkeletonRows(): JSX.Element {
  return (
    <div className="space-y-2 px-3 py-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-md bg-nebula-gray-5 px-3 py-3">
          <div className="h-3 w-3/4 rounded bg-nebula-gray-4 animate-pulse" />
          <div className="mt-2 h-2.5 w-1/2 rounded bg-nebula-gray-4 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// ─────── grouping + formatting helpers ──────────────────────────────────

interface BucketGroup {
  label: string;
  chats: ChatSummaryWire[];
}

const APP_TIMEZONE = 'UTC';

function groupByBucket(chats: ChatSummaryWire[]): BucketGroup[] {
  const today: ChatSummaryWire[] = [];
  const yesterday: ChatSummaryWire[] = [];
  const thisWeek: ChatSummaryWire[] = [];
  const earlier: ChatSummaryWire[] = [];
  const now = new Date();
  const startOfToday = startOfDayAms(now);
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
  const startOfThisWeek = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
  for (const chat of chats) {
    const t = new Date(chat.updatedAt).getTime();
    if (t >= startOfToday.getTime()) today.push(chat);
    else if (t >= startOfYesterday.getTime()) yesterday.push(chat);
    else if (t >= startOfThisWeek.getTime()) thisWeek.push(chat);
    else earlier.push(chat);
  }
  const out: BucketGroup[] = [];
  if (today.length) out.push({ label: 'Today', chats: today });
  if (yesterday.length) out.push({ label: 'Yesterday', chats: yesterday });
  if (thisWeek.length) out.push({ label: 'This week', chats: thisWeek });
  if (earlier.length) out.push({ label: 'Earlier', chats: earlier });
  return out;
}

function startOfDayAms(d: Date): Date {
  // Render the date in app timezone, then parse back to a UTC Date that
  // represents 00:00 on that day. Good enough for bucket boundaries.
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return new Date(`${ymd}T00:00:00Z`);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const today = startOfDayAms(new Date());
  if (d.getTime() >= today.getTime()) {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: APP_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIMEZONE,
    day: 'numeric',
    month: 'short',
  }).format(d);
}
